import { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Activity,
  AlertTriangle,
  CheckCircle,
  FileText,
  Calendar,
  Clipboard,
  Printer,
  Check
} from 'lucide-react';
import type { AnalysisResponse } from '../types/aiAnalysis';
import { exportAnalysisResultToPDF } from '../utils/pdf';
import { Button } from '@/components/ui/button';

interface AnalysisResultCardProps {
  analysis: AnalysisResponse;
  onScheduleFollowUp?: () => void;
}

/**
 * Strip Gemma / chat-template special tokens that occasionally leak into
 * model output (especially when the LLM output post-processor on the
 * backend silently fails). Defensive: a backend fix is preferred but
 * sanitizing here means a bad response renders as empty rather than as
 * a wall of `<end_of_turn>` rows.
 *
 * Also collapses whitespace so the leftover surface text reads cleanly.
 */
const SPECIAL_TOKEN_RE =
  /<\/?\s*(?:end_of_turn|end-of-turn|start_of_turn|start-of-turn|end_of_thought|end-of-thought|start_of_thought|start-of-thought|bos|eos|pad|model|user|system)\b[^>]*>/gi;

const sanitizeLLMText = (raw: unknown): string => {
  if (raw == null) return '';
  const text = typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2);
  return text
    .replace(SPECIAL_TOKEN_RE, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

/**
 * The backend sometimes returns a generic "Analysis completed" placeholder
 * when the LLM extraction step found nothing usable. Treat those as empty
 * so the UI shows a "no result" state instead of a fake summary card.
 */
const PLACEHOLDER_SUMMARIES = [
  /^analysis (completed|finished|done)\b/i,
  /^analysis completed but formatting failed/i,
  /^no (summary|result) (available|found)/i,
];

const isPlaceholderSummary = (s: string): boolean =>
  PLACEHOLDER_SUMMARIES.some((re) => re.test(s.trim()));

export const AnalysisResultCard: React.FC<AnalysisResultCardProps> = ({ analysis, onScheduleFollowUp }) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    summary: true,
    content: false,
    recommendations: true,
    risks: true
  });
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleCopyToClipboard = async () => {
    try {
      // Format recommendations as text
      const recommendationsText = analysis.recommendations
        ?.map((rec, i) => `${i + 1}. ${rec}`)
        .join('\n\n');

      if (recommendationsText) {
        await navigator.clipboard.writeText(recommendationsText);
        setCopiedToClipboard(true);
        setTimeout(() => setCopiedToClipboard(false), 2000);
      }
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const handlePrint = () => {
    // Branded PDF (download + auto-print the PDF) instead of window.print().
    exportAnalysisResultToPDF({
      analysisType: analysis.analysisType,
      summary: analysis.summary,
      recommendations: analysis.recommendations,
      risks: analysis.risks,
      content: analysis.content,
    });
  };

  const getAnalysisTypeLabel = (type: string) => {
    return type.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getAnalysisIcon = (type: string) => {
    if (type.includes('risk')) return '⚠️';
    if (type.includes('lab')) return '🔬';
    if (type.includes('medication')) return '💊';
    if (type.includes('vital')) return '❤️';
    return '🏥';
  };

  // Group risks by severity
  const highRisks = analysis.risks?.filter(r => r.severity === 'high') || [];
  const mediumRisks = analysis.risks?.filter(r => r.severity === 'medium') || [];
  const lowRisks = analysis.risks?.filter(r => r.severity === 'low') || [];

  const confidence = analysis.dataConfidence;
  const dataGaps = analysis.dataGaps ?? [];
  const isInsufficient = confidence === 'insufficient';

  return (
    <div className="space-y-3 w-full">
      {/* Header Badge + confidence chip */}
      <div className="flex items-center gap-2 flex-wrap">
        <div
          className="px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-2"
          style={{
            backgroundColor: 'color-mix(in srgb, var(--color-primary-500) 14%, transparent)',
            color: 'var(--color-primary-700)',
            border: '1px solid color-mix(in srgb, var(--color-primary-500) 25%, transparent)',
          }}
        >
          <span>{getAnalysisIcon(analysis.analysisType)}</span>
          <span>{getAnalysisTypeLabel(analysis.analysisType)}</span>
        </div>
        {confidence && confidence !== 'high' && (
          <div
            className="px-2 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wide flex items-center gap-1"
            style={{
              backgroundColor:
                confidence === 'insufficient'
                  ? 'color-mix(in srgb, var(--color-warning) 14%, transparent)'
                  : confidence === 'low'
                    ? 'color-mix(in srgb, var(--color-warning) 10%, transparent)'
                    : 'color-mix(in srgb, var(--color-text-tertiary) 12%, transparent)',
              color:
                confidence === 'insufficient' || confidence === 'low'
                  ? 'var(--color-warning)'
                  : 'var(--color-text-secondary)',
            }}
            title={dataGaps.length ? dataGaps.join(' · ') : undefined}
          >
            {confidence === 'insufficient' ? '⚠ Insufficient data' : `Low signal · ${confidence}`}
          </div>
        )}
      </div>

      {/* Insufficient-data state: skip the rest of the card and show
          an explicit "needs more data" panel with the list of gaps.
          The user can act on this directly (add labs, sign a visit, etc.) */}
      {isInsufficient && (
        <div
          className="p-4 rounded-lg"
          style={{
            backgroundColor: 'color-mix(in srgb, var(--color-warning) 8%, var(--color-bg-secondary))',
            borderLeft: '4px solid var(--color-warning)',
            border: '1px solid color-mix(in srgb, var(--color-warning) 30%, var(--color-border-primary))',
          }}
        >
          <h4 className="text-sm font-semibold mb-2" style={{ color: 'var(--color-warning)' }}>
            ⚠ Not enough data for this analysis
          </h4>
          <p className="text-sm mb-3" style={{ color: 'var(--color-text-primary)' }}>
            The patient record doesn&apos;t yet contain enough signal to run a
            meaningful {getAnalysisTypeLabel(analysis.analysisType).toLowerCase()}.
            Add the missing data and re-run.
          </p>
          {dataGaps.length > 0 && (
            <ul className="space-y-1 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              {dataGaps.map((gap, i) => (
                <li key={i}>• {gap}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* TL;DR Summary Box */}
      {!isInsufficient && (() => {
        const summary = sanitizeLLMText(analysis.summary);
        const isPlaceholder = !summary || isPlaceholderSummary(summary);
        return (
          <div
            className="p-4 rounded-lg"
            style={{
              backgroundColor: 'color-mix(in srgb, var(--color-primary-500) 8%, var(--color-bg-secondary))',
              borderLeft: '4px solid var(--color-primary-500)',
              border: '1px solid color-mix(in srgb, var(--color-primary-500) 25%, var(--color-border-primary))',
            }}
          >
            <div className="flex items-start gap-3">
              <FileText
                className="w-5 h-5 flex-shrink-0 mt-0.5"
                style={{ color: 'var(--color-primary-500)' }}
              />
              <div className="flex-1">
                <h4
                  className="text-sm font-semibold mb-1"
                  style={{ color: 'var(--color-primary-700)' }}
                >
                  📋 Summary
                </h4>
                {isPlaceholder ? (
                  <p
                    className="text-sm leading-relaxed italic"
                    style={{ color: 'var(--color-text-tertiary)' }}
                  >
                    The model didn't return a usable summary for this request.
                    Try regenerating or narrowing the prompt.
                  </p>
                ) : (
                  <p
                    className="text-sm leading-relaxed whitespace-pre-wrap"
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    {summary}
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* High Priority Risks - Always Visible */}
      {!isInsufficient && highRisks.length > 0 && (
        <div
          className="p-4 rounded-lg"
          style={{
            backgroundColor: 'color-mix(in srgb, var(--color-error) 8%, var(--color-bg-secondary))',
            borderLeft: '4px solid var(--color-error)',
            border: '1px solid color-mix(in srgb, var(--color-error) 30%, var(--color-border-primary))',
          }}
        >
          <div className="flex items-start gap-3">
            <AlertTriangle
              className="w-5 h-5 flex-shrink-0 mt-0.5"
              style={{ color: 'var(--color-error)' }}
            />
            <div className="flex-1">
              <h4
                className="text-sm font-semibold mb-2"
                style={{ color: 'var(--color-error)' }}
              >
                🔴 HIGH PRIORITY RISKS
              </h4>
              <div className="space-y-3">
                {highRisks.map((risk, i) => (
                  <div key={i} className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span
                        className="text-sm font-semibold"
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        {sanitizeLLMText(risk.factor)}
                      </span>
                    </div>
                    <p
                      className="text-xs leading-relaxed ps-4"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      {sanitizeLLMText(risk.description)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recommendations - Actionable */}
      {!isInsufficient && analysis.recommendations && analysis.recommendations.length > 0 && (
        <div
          className="p-4 rounded-lg"
          style={{
            backgroundColor: 'color-mix(in srgb, var(--color-success) 8%, var(--color-bg-secondary))',
            borderLeft: '4px solid var(--color-success)',
            border: '1px solid color-mix(in srgb, var(--color-success) 30%, var(--color-border-primary))',
          }}
        >
          <div className="flex items-start gap-3">
            <CheckCircle
              className="w-5 h-5 flex-shrink-0 mt-0.5"
              style={{ color: 'var(--color-success)' }}
            />
            <div className="flex-1 space-y-3">
              <h4
                className="text-sm font-semibold"
                style={{ color: 'var(--color-success)' }}
              >
                ✅ Recommendations
              </h4>
              <div className="space-y-2">
                {analysis.recommendations.map((rec, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2 p-2 rounded"
                    style={{
                      backgroundColor:
                        'color-mix(in srgb, var(--color-success) 14%, transparent)',
                    }}
                  >
                    <div className="flex-1">
                      <p
                        className="text-xs"
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        • {sanitizeLLMText(rec)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick Action Buttons */}
              <div
                className="flex flex-wrap gap-2 pt-2 border-t"
                style={{
                  borderColor:
                    'color-mix(in srgb, var(--color-success) 30%, var(--color-border-primary))',
                }}
              >
                <Button
                  variant={copiedToClipboard ? 'success' : 'primary'}
                  size="xs"
                  onClick={handleCopyToClipboard}
                  title={copiedToClipboard ? 'Copied!' : 'Copy recommendations to clipboard'}
                >
                  {copiedToClipboard ? (
                    <>
                      <Check />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Clipboard />
                      Copy to Clipboard
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="xs"
                  onClick={onScheduleFollowUp}
                  disabled={!onScheduleFollowUp}
                  title="Schedule a follow-up appointment"
                >
                  <Calendar />
                  Schedule Follow-up
                </Button>
                <Button
                  variant="outline"
                  size="xs"
                  onClick={handlePrint}
                  title="Print analysis results"
                >
                  <Printer />
                  Print
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Medium Priority Risks - Collapsible */}
      {!isInsufficient && mediumRisks.length > 0 && (
        <div
          className="rounded-lg overflow-hidden"
          style={{
            border: '1px solid color-mix(in srgb, var(--color-warning) 30%, var(--color-border-primary))',
          }}
        >
          <button
            onClick={() => toggleSection('mediumRisks')}
            className="w-full p-3 flex items-center justify-between hover:opacity-80 transition-opacity"
            style={{
              backgroundColor:
                'color-mix(in srgb, var(--color-warning) 8%, var(--color-bg-secondary))',
            }}
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" style={{ color: 'var(--color-warning)' }} />
              <span
                className="text-sm font-semibold"
                style={{ color: 'var(--color-warning)' }}
              >
                🟡 Medium Priority Risks ({mediumRisks.length})
              </span>
            </div>
            {expandedSections.mediumRisks ? (
              <ChevronUp className="w-4 h-4" style={{ color: 'var(--color-warning)' }} />
            ) : (
              <ChevronDown className="w-4 h-4" style={{ color: 'var(--color-warning)' }} />
            )}
          </button>
          {expandedSections.mediumRisks && (
            <div
              className="p-4 space-y-2"
              style={{
                backgroundColor:
                  'color-mix(in srgb, var(--color-warning) 14%, transparent)',
              }}
            >
              {mediumRisks.map((risk, i) => (
                <div key={i} className="text-xs space-y-1">
                  <p className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                    {sanitizeLLMText(risk.factor)}
                  </p>
                  <p
                    className="leading-relaxed ps-3"
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    {sanitizeLLMText(risk.description)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Detailed Analysis - Collapsible */}
      {!isInsufficient && (() => {
        const content = sanitizeLLMText(analysis.content);
        if (!content) return null;
        return (
          <div
            className="rounded-lg overflow-hidden"
            style={{ border: '1px solid var(--color-border-primary)' }}
          >
            <button
              onClick={() => toggleSection('content')}
              className="w-full p-3 flex items-center justify-between hover:opacity-80 transition-opacity"
              style={{ backgroundColor: 'var(--color-bg-secondary)' }}
            >
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4" style={{ color: 'var(--color-text-tertiary)' }} />
                <span
                  className="text-sm font-semibold"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  Detailed Analysis
                </span>
              </div>
              {expandedSections.content ? (
                <ChevronUp className="w-4 h-4" style={{ color: 'var(--color-text-tertiary)' }} />
              ) : (
                <ChevronDown className="w-4 h-4" style={{ color: 'var(--color-text-tertiary)' }} />
              )}
            </button>
            {expandedSections.content && (
              <div
                className="p-4"
                style={{
                  backgroundColor:
                    'color-mix(in srgb, var(--color-text-primary) 4%, var(--color-bg-secondary))',
                }}
              >
                <div
                  className="text-xs leading-relaxed whitespace-pre-wrap"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  {content}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Low Priority Risks - Collapsed by Default */}
      {!isInsufficient && lowRisks.length > 0 && (
        <div
          className="rounded-lg overflow-hidden"
          style={{
            border: '1px solid color-mix(in srgb, var(--color-primary-500) 25%, var(--color-border-primary))',
          }}
        >
          <button
            onClick={() => toggleSection('lowRisks')}
            className="w-full p-3 flex items-center justify-between hover:opacity-80 transition-opacity"
            style={{
              backgroundColor:
                'color-mix(in srgb, var(--color-primary-500) 8%, var(--color-bg-secondary))',
            }}
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" style={{ color: 'var(--color-primary-600)' }} />
              <span
                className="text-sm font-semibold"
                style={{ color: 'var(--color-primary-700)' }}
              >
                🟢 Low Priority Risks ({lowRisks.length})
              </span>
            </div>
            {expandedSections.lowRisks ? (
              <ChevronUp className="w-4 h-4" style={{ color: 'var(--color-primary-600)' }} />
            ) : (
              <ChevronDown className="w-4 h-4" style={{ color: 'var(--color-primary-600)' }} />
            )}
          </button>
          {expandedSections.lowRisks && (
            <div
              className="p-4 space-y-2"
              style={{
                backgroundColor:
                  'color-mix(in srgb, var(--color-primary-500) 14%, transparent)',
              }}
            >
              {lowRisks.map((risk, i) => (
                <div key={i} className="text-xs space-y-1">
                  <p className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                    {sanitizeLLMText(risk.factor)}
                  </p>
                  <p
                    className="leading-relaxed ps-3"
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    {sanitizeLLMText(risk.description)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
